import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import ResizableSidebar from "./ResizableSidebar";
import ImportPanel from "./panels/ImportPanel";
import PresetsPanel from "./panels/PresetsPanel";
import EmitterPanel from "./panels/EmitterPanel";
import TargetingPanel from "./panels/TargetingPanel";
import SpritePanel from "./panels/SpritePanel";
import ParticlesPanel from "./panels/ParticlesPanel";
import OrbitPanel from "./panels/OrbitPanel";
import RotationPanel from "./panels/RotationPanel";
import SubEmittersPanel from "./panels/SubEmittersPanel";
import GravityPanel from "./panels/GravityPanel";
import CollisionPanel from "./panels/CollisionPanel";
import EditorUIPanel from "./panels/EditorUIPanel";
import GlowPanel from "./panels/GlowPanel";
import exportPreset from "../utils/exportPreset";
import { applyPresetFromText } from "../utils/PresetApply";

(() => {
  if (typeof window === "undefined") return;
  if (window.__ET_LS_SANDBOX_INSTALLED) return;
  window.__ET_LS_SANDBOX_INSTALLED = true;
  const LS = window.localStorage;
  if (!LS || !LS.getItem || !LS.setItem) return;
  const allowOpenFlag = (key) =>
    typeof key === "string" &&
    key.startsWith("EmitterTool:") &&
    (key.endsWith("PanelOpenV1") ||
      key.endsWith("OpenV1") ||
      key.endsWith("Open"));
  const editorKeys = new Set([
    "EmitterTool:UI:hideFog",
    "EmitterTool:UI:fireMode",
    "EmitterTool:UI:timedDur",
    "EmitterTool:UI:lastPlay",
    "EmitterTool:UI:wallsEnabled",
    "EmitterTool:UI:tokenEnabled",
    "EmitterTool:UI:enemyTokenEnabled",
    "EmitterTool:UI:showGrid",
    "EmitterTool:UI:showShape",
    "EmitterTool:UI:gridSettingsV1",
    "EmitterTool:UI:pickedOriginX",
    "EmitterTool:UI:pickedOriginY",
    "EmitterTool:UI:pickedAngleDeg",
    "EmitterTool:UI:ruleStyle",
    "EmitterTool:UI:ruleDur",
    "EmitterTool:targeting:mode",
    "EmitterTool:targeting:rangeM",
    "EmitterTool:targeting:headSpeedMps",
  ]);
  const isWhitelisted = (key) =>
    typeof key === "string" &&
    key.startsWith("EmitterTool:") &&
    (allowOpenFlag(key) || editorKeys.has(key));
  const origGet = LS.getItem.bind(LS);
  const origSet = LS.setItem.bind(LS);
  const origRemove = LS.removeItem.bind(LS);
  LS.getItem = (key) => {
    try {
      if (!key || typeof key !== "string") return origGet(key);
      if (!key.startsWith("EmitterTool:")) return origGet(key);
      return isWhitelisted(key) ? origGet(key) : null;
    } catch {
      return null;
    }
  };
  LS.setItem = (key, value) => {
    try {
      if (!key || typeof key !== "string") return origSet(key, value);
      if (!key.startsWith("EmitterTool:")) return origSet(key, value);
      if (isWhitelisted(key)) return origSet(key, value);
      return;
    } catch {
      return;
    }
  };
  LS.removeItem = (key) => {
    try {
      if (!key || typeof key !== "string") return origRemove(key);
      if (!key.startsWith("EmitterTool:")) return origRemove(key);
      if (isWhitelisted(key)) return origRemove(key);
      return;
    } catch {
      return;
    }
  };
})();

const ensureArray = (a) => (Array.isArray(a) ? a : []);
const deepClone = (v) => JSON.parse(JSON.stringify(v ?? null));
const findChildIndex = (es, id) =>
  ensureArray(es?.subEmitters).findIndex((s) => (s?.id ?? "") === id);

const emit = (name, detail) => {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {}
};

function mergeSnapshot(base, patch) {
  const out = { ...(base || {}) };
  if (!patch || typeof patch !== "object") return out;
  for (const k of Object.keys(patch)) {
    const sv = patch[k];
    const tv = out[k];
    if (sv && typeof sv === "object" && !Array.isArray(sv)) {
      out[k] = mergeSnapshot(tv || {}, sv);
    } else {
      out[k] = sv;
    }
  }
  return out;
}

function materialiseChildOverrides(
  baseES,
  idx,
  parentShape,
  parentShapeSettings,
  parentES,
  parentSprite
) {
  const list = ensureArray(baseES?.subEmitters).slice();
  if (idx < 0 || idx >= list.length) return baseES;
  const item = { ...(list[idx] || {}) };
  const ovIn = { ...(item.overrides || {}) };
  if (!ovIn.__materialized) {
    ovIn.emitterShape = ovIn.emitterShape ?? deepClone(parentShape);
    ovIn.shapeSettings =
      ovIn.shapeSettings ?? deepClone(parentShapeSettings);
    ovIn.effectSettings = ovIn.effectSettings ?? deepClone(parentES);
    ovIn.selectedSprite = ovIn.selectedSprite ?? deepClone(parentSprite);

    if (Array.isArray(parentES?.selectedSprites) && !ovIn.selectedSprites) {
      ovIn.selectedSprites = deepClone(parentES.selectedSprites);
    }
    if (
      Array.isArray(parentES?.selectedAnimFrames) &&
      !ovIn.selectedAnimFrames
    ) {
      ovIn.selectedAnimFrames = deepClone(parentES.selectedAnimFrames);
    }
    if (Number.isFinite(parentES?.animFps) && ovIn.animFps == null) {
      ovIn.animFps = Number(parentES.animFps);
    }

    ovIn.__materialized = true;
  }
  item.overrides = ovIn;
  list[idx] = item;
  return { ...baseES, subEmitters: list };
}

function materialiseSeqOverrides(
  baseES,
  stepIdx,
  parentShape,
  parentShapeSettings,
  parentES,
  parentSprite
) {
  const seq = baseES?.sequencer || {};
  const steps = ensureArray(seq.steps).slice();
  if (stepIdx < 0 || stepIdx >= steps.length) return baseES;
  const step = { ...(steps[stepIdx] || {}) };
  const ovIn = { ...(step.overrides || {}) };
  if (!ovIn.__materialized) {
    ovIn.emitterShape = ovIn.emitterShape ?? deepClone(parentShape);
    ovIn.shapeSettings =
      ovIn.shapeSettings ?? deepClone(parentShapeSettings);
    ovIn.effectSettings = ovIn.effectSettings ?? deepClone(parentES);
    ovIn.selectedSprite = ovIn.selectedSprite ?? deepClone(parentSprite);

    if (Array.isArray(parentES?.selectedSprites) && !ovIn.selectedSprites) {
      ovIn.selectedSprites = deepClone(parentES.selectedSprites);
    }
    if (
      Array.isArray(parentES?.selectedAnimFrames) &&
      !ovIn.selectedAnimFrames
    ) {
      ovIn.selectedAnimFrames = deepClone(parentES.selectedAnimFrames);
    }
    if (Number.isFinite(parentES?.animFps) && ovIn.animFps == null) {
      ovIn.animFps = Number(parentES.animFps);
    }

    ovIn.__materialized = true;
  }
  step.overrides = ovIn;
  steps[stepIdx] = step;
  return { ...baseES, sequencer: { ...seq, steps } };
}

function useScopedEmitters({
  activeEmitterRef,
  effectSettings,
  setEffectSettings,
  emitterShape,
  setEmitterShape,
  shapeSettings,
  setShapeSettings,
  selectedSprite,
  setSelectedSprite,
}) {
  const isChildScope = activeEmitterRef?.type === "child";
  const isSeqScope = activeEmitterRef?.type === "sequence";
  const childId = isChildScope ? activeEmitterRef.id : null;
  const stepId = isSeqScope ? activeEmitterRef.stepId : null;

  const childInitRef = React.useRef(new Set());
  const seqInitRef = React.useRef(new Set());

  useEffect(() => {
    if (!isChildScope || !childId) return;
    if (childInitRef.current.has(childId)) return;
    childInitRef.current.add(childId);

    setEffectSettings((cur) => {
      const base = cur ?? {};
      const idx = findChildIndex(base, childId);
      if (idx < 0) return base;
      const nextES = materialiseChildOverrides(
        base,
        idx,
        emitterShape,
        shapeSettings,
        effectSettings,
        selectedSprite
      );
      return nextES;
    });
  }, [
    isChildScope,
    childId,
    setEffectSettings,
    emitterShape,
    shapeSettings,
    effectSettings,
    selectedSprite,
  ]);

  useEffect(() => {
    if (!isSeqScope || !stepId) return;
    if (seqInitRef.current.has(stepId)) return;
    seqInitRef.current.add(stepId);

    setEffectSettings((cur) => {
      const base = cur ?? {};
      const seq = base.sequencer || {};
      const steps = ensureArray(seq.steps);
      const idx = steps.findIndex((s) => (s?.id ?? "") === stepId);
      if (idx < 0) return base;
      const nextES = materialiseSeqOverrides(
        base,
        idx,
        emitterShape,
        shapeSettings,
        effectSettings,
        selectedSprite
      );
      return nextES;
    });
  }, [
    isSeqScope,
    stepId,
    setEffectSettings,
    emitterShape,
    shapeSettings,
    effectSettings,
    selectedSprite,
  ]);

  const scoped = useMemo(() => {
    if (!isChildScope && !isSeqScope) {
      return {
        scopeType: "parent",
        scopeId: "parent",
        emitterShape,
        shapeSettings,
        effectSettings,
        selectedSprite,
        selectedSprites: ensureArray(effectSettings?.selectedSprites),
        selectedAnimFrames: ensureArray(
          effectSettings?.selectedAnimFrames
        ),
        animFps: Number.isFinite(effectSettings?.animFps)
          ? effectSettings.animFps
          : 12,
      };
    }

    if (isChildScope) {
      const list = ensureArray(effectSettings?.subEmitters);
      const idx = list.findIndex((s) => (s?.id ?? "") === childId);
      const item = idx >= 0 ? list[idx] : null;
      const ov = item?.overrides ?? {};
      const esEff = ov?.effectSettings ?? effectSettings;
      return {
        scopeType: "child",
        scopeId: childId,
        childIndex: idx,
        child: item,
        emitterShape: ov?.emitterShape ?? emitterShape,
        shapeSettings: ov?.shapeSettings ?? shapeSettings,
        effectSettings: esEff,
        selectedSprite: ov?.selectedSprite ?? selectedSprite,
        selectedSprites: ensureArray(
          ov?.selectedSprites ?? esEff?.selectedSprites
        ),
        selectedAnimFrames: ensureArray(
          esEff?.selectedAnimFrames ?? ov?.selectedAnimFrames
        ),
        animFps: Number.isFinite(esEff?.animFps) ? esEff.animFps : 12,
      };
    }

    if (isSeqScope) {
      const seq = effectSettings?.sequencer || {};
      const steps = ensureArray(seq.steps);
      const idx = steps.findIndex((s) => (s?.id ?? "") === stepId);
      const step = idx >= 0 ? steps[idx] : null;
      const ov = step?.overrides || {};
      const esEff = ov?.effectSettings ?? effectSettings;
      return {
        scopeType: "sequence",
        scopeId: stepId,
        stepIndex: idx,
        step,
        emitterShape: ov?.emitterShape ?? emitterShape,
        shapeSettings: ov?.shapeSettings ?? shapeSettings,
        effectSettings: esEff,
        selectedSprite: ov?.selectedSprite ?? selectedSprite,
        selectedSprites: ensureArray(
          ov?.selectedSprites ?? esEff?.selectedSprites
        ),
        selectedAnimFrames: ensureArray(
          ov?.selectedAnimFrames ?? esEff?.selectedAnimFrames
        ),
        animFps: Number.isFinite(ov?.animFps ?? esEff?.animFps)
          ? Number(ov.animFps ?? esEff.animFps)
          : 12,
      };
    }

    return {
      scopeType: "parent",
      scopeId: "parent",
      emitterShape,
      shapeSettings,
      effectSettings,
      selectedSprite,
      selectedSprites: ensureArray(effectSettings?.selectedSprites),
      selectedAnimFrames: ensureArray(
        effectSettings?.selectedAnimFrames
      ),
      animFps: Number.isFinite(effectSettings?.animFps)
        ? effectSettings.animFps
        : 12,
    };
  }, [
    isChildScope,
    isSeqScope,
    childId,
    stepId,
    effectSettings,
    emitterShape,
    shapeSettings,
    selectedSprite,
  ]);

  const writeOverrides = useCallback(
    (patch) => {
      if (!isChildScope) return;
      const cid = childId;
      setEffectSettings((cur) => {
        const base = cur ?? {};
        const list = ensureArray(base.subEmitters).slice();
        const idx = findChildIndex(base, cid);
        if (idx < 0) return base;
        const item = { ...(list[idx] || {}) };
        const ov = { ...(item.overrides || {}) };
        const nextOv =
          typeof patch === "function"
            ? patch(ov)
            : mergeSnapshot(ov, patch);
        item.overrides = nextOv;
        list[idx] = item;
        return { ...base, subEmitters: list };
      });
    },
    [isChildScope, childId, setEffectSettings]
  );

  const writeSeqOverrides = useCallback(
    (patch) => {
      if (!isSeqScope) return;
      const sid = stepId;
      setEffectSettings((cur) => {
        const base = cur ?? {};
        const seq = base.sequencer || {};
        const steps = ensureArray(seq.steps).slice();
        const idx = steps.findIndex((s) => (s?.id ?? "") === sid);
        if (idx < 0) return base;
        const step = { ...(steps[idx] || {}) };
        const ov = { ...(step.overrides || {}) };
        const nextOv =
          typeof patch === "function"
            ? patch(ov)
            : mergeSnapshot(ov, patch);
        step.overrides = nextOv;
        steps[idx] = step;
        return { ...base, sequencer: { ...seq, steps } };
      });
    },
    [isSeqScope, stepId, setEffectSettings]
  );

  const scopedSetters = isChildScope
    ? {
        setEmitterShape: (v) => writeOverrides({ emitterShape: v }),
        setShapeSettings: (patch) =>
          writeOverrides((ov) => ({
            ...ov,
            shapeSettings:
              typeof patch === "function"
                ? patch(ov.shapeSettings ?? {})
                : { ...(ov.shapeSettings ?? {}), ...patch },
          })),
        setEffectSettings: (patch) =>
          writeOverrides((ov) => ({
            ...ov,
            effectSettings:
              typeof patch === "function"
                ? patch(ov.effectSettings ?? {})
                : { ...(ov.effectSettings ?? {}), ...patch },
          })),
        setSelectedSprite: (v) =>
          writeOverrides({ selectedSprite: v }),
        setSelectedSprites: (arr) =>
          writeOverrides((ov) => ({
            ...ov,
            selectedSprites: ensureArray(arr),
            effectSettings: {
              ...(ov.effectSettings ?? {}),
              selectedSprites: ensureArray(arr),
            },
          })),
        setSelectedAnimFrames: (arr) =>
          writeOverrides((ov) => ({
            ...ov,
            selectedAnimFrames: ensureArray(arr),
            effectSettings: {
              ...(ov.effectSettings ?? {}),
              selectedAnimFrames: ensureArray(arr),
              spriteAnimatedEnabled:
                ensureArray(arr).length >= 2,
            },
          })),
        setAnimFps: (fps) =>
          writeOverrides((ov) => ({
            ...ov,
            animFps: Number(fps) || 12,
            effectSettings: {
              ...(ov.effectSettings ?? {}),
              animFps: Number(fps) || 12,
            },
          })),
      }
    : isSeqScope
    ? {
        setEmitterShape: (v) =>
          writeSeqOverrides({ emitterShape: v }),
        setShapeSettings: (patch) =>
          writeSeqOverrides((ov) => ({
            ...ov,
            shapeSettings:
              typeof patch === "function"
                ? patch(ov.shapeSettings ?? {})
                : { ...(ov.shapeSettings ?? {}), ...patch },
          })),
        setEffectSettings: (patch) =>
          writeSeqOverrides((ov) => ({
            ...ov,
            effectSettings:
              typeof patch === "function"
                ? patch(ov.effectSettings ?? {})
                : { ...(ov.effectSettings ?? {}), ...patch },
          })),
        setSelectedSprite: (v) =>
          writeSeqOverrides({ selectedSprite: v }),
        setSelectedSprites: (arr) =>
          writeSeqOverrides((ov) => ({
            ...ov,
            selectedSprites: ensureArray(arr),
            effectSettings: {
              ...(ov.effectSettings ?? {}),
              selectedSprites: ensureArray(arr),
            },
          })),
        setSelectedAnimFrames: (arr) =>
          writeSeqOverrides((ov) => ({
            ...ov,
            selectedAnimFrames: ensureArray(arr),
            effectSettings: {
              ...(ov.effectSettings ?? {}),
              selectedAnimFrames: ensureArray(arr),
              spriteAnimatedEnabled:
                ensureArray(arr).length >= 2,
            },
          })),
        setAnimFps: (fps) =>
          writeSeqOverrides((ov) => ({
            ...ov,
            animFps: Number(fps) || 12,
            effectSettings: {
              ...(ov.effectSettings ?? {}),
              animFps: Number(fps) || 12,
            },
          })),
      }
    : {
        setEmitterShape,
        setShapeSettings,
        setEffectSettings,
        setSelectedSprite,
        setSelectedSprites: (arr) =>
          setEffectSettings((es) => ({
            ...(es || {}),
            selectedSprites: ensureArray(arr),
          })),
        setSelectedAnimFrames: (arr) =>
          setEffectSettings((es) => ({
            ...(es || {}),
            selectedAnimFrames: ensureArray(arr),
            spriteAnimatedEnabled:
              ensureArray(arr).length >= 2,
          })),
        setAnimFps: (fps) =>
          setEffectSettings((es) => ({
            ...(es || {}),
            animFps: Number(fps) || 12,
          })),
      };

  const cloneParentIntoChild = useCallback(() => {
    if (!isChildScope) return;
    const id = childId;
    setEffectSettings((cur) => {
      const base = cur ?? {};
      const list = ensureArray(base.subEmitters).slice();
      const idx = findChildIndex(base, id);
      if (idx < 0) return base;
      const item = { ...(list[idx] || {}) };
      const ov = { ...(item.overrides || {}) };
      if (!ov.__materialized) {
        ov.emitterShape =
          ov.emitterShape ?? deepClone(emitterShape);
        ov.shapeSettings =
          ov.shapeSettings ?? deepClone(shapeSettings);
        ov.effectSettings =
          ov.effectSettings ?? deepClone(effectSettings);
        ov.selectedSprite =
          ov.selectedSprite ?? deepClone(selectedSprite);
        if (
          Array.isArray(effectSettings?.selectedSprites) &&
          !ov.selectedSprites
        ) {
          ov.selectedSprites = deepClone(
            effectSettings.selectedSprites
          );
        }
        if (
          Array.isArray(effectSettings?.selectedAnimFrames) &&
          !ov.selectedAnimFrames
        ) {
          ov.selectedAnimFrames = deepClone(
            effectSettings.selectedAnimFrames
          );
        }
        if (
          Number.isFinite(effectSettings?.animFps) &&
          ov.animFps == null
        ) {
          ov.animFps = Number(effectSettings.animFps);
        }
        ov.__materialized = true;
        item.overrides = ov;
        list[idx] = item;
        return { ...base, subEmitters: list };
      }
      return base;
    });
  }, [
    isChildScope,
    childId,
    setEffectSettings,
    emitterShape,
    shapeSettings,
    effectSettings,
    selectedSprite,
  ]);

  return {
    scoped,
    setEmitterShape: scopedSetters.setEmitterShape,
    setShapeSettings: scopedSetters.setShapeSettings,
    setEffectSettings: scopedSetters.setEffectSettings,
    setSelectedSprite: scopedSetters.setSelectedSprite,
    setSelectedSprites: scopedSetters.setSelectedSprites,
    setSelectedAnimFrames: scopedSetters.setSelectedAnimFrames,
    setAnimFps: scopedSetters.setAnimFps,
    cloneParentIntoChild,
  };
}

function EffectsSidebar(props) {
  const {
    effectName,
    setEffectName,
    onExport,
    onImport,
    applyPresetText,
    effects,
    effectKey,
    setEffectKey,
    atlas,
    selectedSprite,
    setSelectedSprite,
    wallsEnabled,
    setWallsEnabled,
    tokenEnabled,
    setTokenEnabled,
    enemyEnabled,
    setEnemyEnabled,
    effectSettings,
    setEffectSettings,
    panelTitle,
    emitterShape,
    setEmitterShape,
    shapeSettings,
    setShapeSettings,
    showShape,
    setShowShape,
    showGrid,
    setShowGrid,
    pxPerMeter,
    setPxPerMeter,
    layers,
    setLayers,
    activeLayerIndex,
    setActiveLayerIndex,
    onAddChildFromParent,
    onRemoveLayer,
    onUpdateLayer,
    onReorderLayers,
    onToggleFogHidden,
    onPresetReady,
  } = props;

  const [activeEmitterRef, setActiveEmitterRef] = useState({
    type: "parent",
  });
  const [remountTick, setRemountTick] = useState(0);
  const seqGuardUntilRef = useRef(0);

  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail || {};
      try {
        window.dispatchEvent(
          new CustomEvent(
            "EmitterTool:Runtime:SubEmitterRule",
            { detail: d }
          )
        );
      } catch {}
    };
    window.addEventListener("EmitterTool:SubEmitterRule", handler);
    return () => {
      window.removeEventListener(
        "EmitterTool:SubEmitterRule",
        handler
      );
    };
  }, []);

  useEffect(() => {
    const onSel = (e) => {
      const id = e?.detail?.stepId;
      if (!id) return;
      seqGuardUntilRef.current = Date.now() + 800;
      setActiveEmitterRef({ type: "sequence", stepId: id });
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:ScopeChanged", {
            detail: { type: "sequence", stepId: id },
          })
        );
      } catch {}
    };
    window.addEventListener(
      "EmitterTool:Sequencer:SelectStep",
      onSel
    );
    return () => {
      window.removeEventListener(
        "EmitterTool:Sequencer:SelectStep",
        onSel
      );
    };
  }, []);

  useEffect(() => {
    const onTargetingPreset = (e) => {
      const id = (e?.detail?.presetId || "")
        .toString()
        .toLowerCase();
      if (!id) return;
      try {
        localStorage.setItem("EmitterTool:targeting:mode", id);
      } catch {}
    };
    const onRange = (e) => {
      const v = Number(e?.detail?.rangeM);
      if (!Number.isFinite(v)) return;
      try {
        localStorage.setItem(
          "EmitterTool:targeting:rangeM",
          String(v)
        );
      } catch {}
    };
    const onTttCfg = (e) => {
      const v = Number(e?.detail?.headSpeedMps);
      if (!Number.isFinite(v)) return;
      try {
        localStorage.setItem(
          "EmitterTool:targeting:headSpeedMps",
          String(v)
        );
      } catch {}
    };
    window.addEventListener(
      "EmitterTool:UI:TargetingPreset",
      onTargetingPreset
    );
    window.addEventListener(
      "EmitterTool:Targeting:SetRangeM",
      onRange
    );
    window.addEventListener(
      "EmitterTool:UI:TokenToTargetConfig",
      onTttCfg
    );
    return () => {
      window.removeEventListener(
        "EmitterTool:UI:TargetingPreset",
        onTargetingPreset
      );
      window.removeEventListener(
        "EmitterTool:Targeting:SetRangeM",
        onRange
      );
      window.removeEventListener(
        "EmitterTool:UI:TokenToTargetConfig",
        onTttCfg
      );
    };
  }, []);

  const rehydrateTargetingFromLS = useCallback(() => {
    let mode = "global";
    let range = undefined;
    let speed = undefined;
    try {
      const m = localStorage.getItem(
        "EmitterTool:targeting:mode"
      );
      if (m) mode = m.toLowerCase();
    } catch {}
    try {
      const r = Number(
        localStorage.getItem(
          "EmitterTool:targeting:rangeM"
        )
      );
      if (Number.isFinite(r)) range = r;
    } catch {}
    try {
      const s = Number(
        localStorage.getItem(
          "EmitterTool:targeting:headSpeedMps"
        )
      );
      if (Number.isFinite(s)) speed = s;
    } catch {}
    emit("EmitterTool:Targeting:Hydrate", {
      mode,
      rangeM: range,
      headSpeedMps: speed,
      targeting: {
        mode,
        rangeM: range,
        headSpeedMps: speed,
      },
    });
  }, []);

  const handleImport = useCallback(
    (fileText) => {
      applyPresetFromText(fileText, {
        effects,
        effectKey,
        setEffectKey,
        setEffectName,
        setSelectedSprite,
        setEffectSettings,
        setEmitterShape,
        setShapeSettings,
        setInfinite: (v) => {
          try {
            localStorage.setItem(
              "EmitterTool:UI:fireMode",
              v ? "infinite" : "off"
            );
          } catch {}
        },
        setInfiniteRate: (v) => {
          try {
            localStorage.setItem(
              "EmitterTool:UI:infiniteRate",
              String(v)
            );
          } catch {}
        },
        setLayers,
        setShowGrid,
        setPxPerMeter,
        setWallsEnabled,
        setTokenEnabled,
        emitterShape,
        selectedSprite,
        effectSettings,
        setActiveLayerIndex,
        setShowShape,
      });
      setRemountTick((t) => t + 1);
      setTimeout(() => {
        rehydrateTargetingFromLS();
      }, 0);
    },
    [
      effects,
      effectKey,
      setEffectKey,
      setEffectName,
      setSelectedSprite,
      setEffectSettings,
      setEmitterShape,
      setShapeSettings,
      setLayers,
      setShowGrid,
      setPxPerMeter,
      setWallsEnabled,
      setTokenEnabled,
      emitterShape,
      selectedSprite,
      effectSettings,
      setActiveLayerIndex,
      setShowShape,
      rehydrateTargetingFromLS,
    ]
  );

  const onExportEffective = useCallback(() => {
    exportPreset(
      effectName,
      effectKey,
      pxPerMeter,
      showGrid,
      {
        emitterShape,
        shapeSettings,
        effectSettings,
        selectedSprite,
        showShape,
      },
      layers
    );
  }, [
    effectName,
    effectKey,
    pxPerMeter,
    showGrid,
    emitterShape,
    shapeSettings,
    effectSettings,
    selectedSprite,
    showShape,
    layers,
  ]);

  const atlasStickyRef = useRef(null);
  const effectiveAtlas = useMemo(() => {
    const incoming = atlas;
    const texMap =
      incoming?.textures
        ? incoming.textures
        : incoming && typeof incoming === "object"
        ? incoming
        : null;
    const hasTex = texMap && Object.keys(texMap).length > 0;
    if (hasTex) {
      atlasStickyRef.current = incoming;
    }
    return hasTex ? incoming : atlasStickyRef.current;
  }, [atlas]);

  const {
    scoped,
    setEmitterShape: scopedSetEmitterShape,
    setShapeSettings: scopedSetShapeSettings,
    setEffectSettings: scopedSetEffectSettings,
    setSelectedSprite: scopedSetSelectedSprite,
    setSelectedSprites: scopedSetSelectedSprites,
    setSelectedAnimFrames: scopedSetSelectedAnimFrames,
    setAnimFps: scopedSetAnimFps,
    cloneParentIntoChild,
  } = useScopedEmitters({
    activeEmitterRef,
    effectSettings,
    setEffectSettings,
    emitterShape,
    setEmitterShape,
    shapeSettings,
    setShapeSettings,
    selectedSprite,
    setSelectedSprite,
  });

  const onSelectEmitter = useCallback(
    (ref) => {
      const now = Date.now();
      if (
        ref?.type === "parent" &&
        now < seqGuardUntilRef.current
      ) {
        return;
      }
      setActiveEmitterRef(ref);

      if (ref?.type === "child") {
        cloneParentIntoChild();
      }

      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:ScopeChanged", {
            detail: ref,
          })
        );
      } catch {}

      setRemountTick((t) => t + 1);
    },
    [cloneParentIntoChild]
  );

  const ActiveBar = useMemo(() => {
    let label;
    if (activeEmitterRef?.type === "parent") {
      label = "Editing: Parent";
    } else if (activeEmitterRef?.type === "child") {
      label = `Editing: Sub-Emitter (${activeEmitterRef.id})`;
    } else if (activeEmitterRef?.type === "sequence") {
      label = `Editing: Sequencer Step (${activeEmitterRef.stepId})`;
    } else {
      label = "Editing";
    }
    return (
      <div
        style={{
          margin: "0 0 8px",
          padding: "6px 8px",
          fontSize: 12,
          border: "1px dashed #334155",
          borderRadius: 6,
          background: "#0c121c",
        }}
      >
        {label}
      </div>
    );
  }, [activeEmitterRef]);

  const scopedEmitterShape = scoped.emitterShape;
  const scopedShapeSettings = scoped.shapeSettings;
  const scopedEffectSettings = scoped.effectSettings;
  const scopedSelectedSprite = scoped.selectedSprite;
  const scopedSelectedSprites = scoped.selectedSprites;

  function buildCurrentPreset() {
    const parentShape = emitterShape;
    const parentShapeSettings = deepClone(shapeSettings) || {};
    const parentES = deepClone(effectSettings) || {};

    if (selectedSprite != null && !parentES.selectedSprite) {
      parentES.selectedSprite = selectedSprite;
    }

    const rawSubs = ensureArray(effectSettings?.subEmitters);
    const parentIndexRaw = Number.isFinite(
      effectSettings?.subEmittersParentIndex
    )
      ? effectSettings.subEmittersParentIndex
      : 0;
    const clampedParentIndex = Math.min(
      Math.max(0, Math.floor(parentIndexRaw)),
      rawSubs.length
    );

    function buildLayerFromSubEmitter(sub) {
      const ov = sub && sub.overrides ? sub.overrides : {};

      const layerEmitterShape =
        ov.emitterShape != null
          ? ov.emitterShape
          : parentShape;
      const layerShapeSettings =
        ov.shapeSettings != null
          ? deepClone(ov.shapeSettings)
          : deepClone(parentShapeSettings) || {};
      const layerEffectSettings =
        ov.effectSettings != null
          ? deepClone(ov.effectSettings)
          : deepClone(parentES) || {};

      const selOv = {};
      if (ov.selectedSprite != null)
        selOv.selectedSprite = ov.selectedSprite;
      if (Array.isArray(ov.selectedSprites))
        selOv.selectedSprites = deepClone(ov.selectedSprites);
      if (Array.isArray(ov.selectedAnimFrames))
        selOv.selectedAnimFrames = deepClone(
          ov.selectedAnimFrames
        );
      if (ov.animFps != null) selOv.animFps = ov.animFps;

      const outLayer = {
        id: sub.id || "",
        enabled: sub.enabled !== false,
        startMode: sub.startMode || "immediate",
        delayMs: Number.isFinite(+sub.delayMs)
          ? +sub.delayMs
          : 0,
        runForMode: sub.runForMode || "inherit",
        runDurationS: Number.isFinite(+sub.runDurationS)
          ? +sub.runDurationS
          : 0.5,
        aboveParent: true,
        emitterShape: layerEmitterShape,
        shapeSettings: layerShapeSettings,
        effectSettings: layerEffectSettings,
        __selectionOverrides:
          Object.keys(selOv).length > 0 ? selOv : null,
      };

      console.log(
        "[EffectsSidebar] buildLayerFromSubEmitter",
        outLayer.id,
        {
          startMode: outLayer.startMode,
          delayMs: outLayer.delayMs,
          runForMode: outLayer.runForMode,
          runDurationS: outLayer.runDurationS,
          enabled: outLayer.enabled,
        }
      );

      return outLayer;
    }

    const bakedChildren = rawSubs.map((sub) =>
      buildLayerFromSubEmitter(sub)
    );

    const visualStack = [];
    for (let i = 0; i < bakedChildren.length; i++) {
      if (i === clampedParentIndex) {
        visualStack.push("__PARENT__");
      }
      visualStack.push(bakedChildren[i]);
    }
    if (clampedParentIndex >= bakedChildren.length) {
      visualStack.push("__PARENT__");
    }

    const layersOut = [];
    let passedParent = false;
    for (let i = 0; i < visualStack.length; i++) {
      const entry = visualStack[i];
      if (entry === "__PARENT__") {
        passedParent = true;
        continue;
      }
      const layer = entry;
      layer.aboveParent = passedParent;
      layersOut.push(layer);
    }

    return {
      parent: {
        emitterShape: parentShape,
        shapeSettings: parentShapeSettings,
        effectSettings: parentES,
      },
      layers: layersOut,
      pxPerMeter: Number.isFinite(pxPerMeter)
        ? pxPerMeter
        : undefined,
      rule: {},
    };
  }

  useEffect(() => {
    if (typeof onPresetReady === "function") {
      try {
        const presetNow = buildCurrentPreset();
        console.log(
          "[EffectsSidebar] onPresetReady() layers summary:",
          presetNow.layers.map((L) => ({
            id: L.id,
            startMode: L.startMode,
            delayMs: L.delayMs,
            runForMode: L.runForMode,
            runDurationS: L.runDurationS,
            enabled: L.enabled,
            aboveParent: L.aboveParent,
          }))
        );
        onPresetReady(presetNow);
      } catch {}
    }
  }, [
    onPresetReady,
    emitterShape,
    shapeSettings,
    selectedSprite,
    pxPerMeter,
    effectSettings,
    remountTick,
  ]);

  return (
    <ResizableSidebar
      data-et-ui="sidebar"
      style={{ overflowX: "hidden", maxWidth: "100%" }}
      onClickCapture={(e) => { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); }}
      onMouseDownCapture={(e) => { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); }}
      onPointerDownCapture={(e) => { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); }}
      onTouchStartCapture={(e) => { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); }}
      onWheelCapture={(e) => { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); }}
    >
      <h3
        style={{
          margin: "4px 0 12px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {panelTitle ? String(panelTitle) : "Emitter Tool"}
      </h3>

      <ImportPanel
        key={`import-${remountTick}`}
        effectName={effectName}
        setEffectName={setEffectName}
        onExport={onExportEffective}
        onImport={handleImport}
        defaultOpen={true}
      />

      <PresetsPanel
        key={`presets-${remountTick}`}
        applyPresetText={applyPresetText}
      />

      <SubEmittersPanel
        key={`subemit-${remountTick}`}
        effectSettings={effectSettings}
        setEffectSettings={setEffectSettings}
        effects={effects}
        activeEmitterRef={activeEmitterRef}
        onSelectEmitter={onSelectEmitter}
        parentSnapshot={{
          emitterShape,
          shapeSettings,
          effectSettings,
          selectedSprite,
          selectedSprites: ensureArray(
            effectSettings?.selectedSprites
          ),
        }}
      />

      <div
        style={{
          margin: "0 0 8px",
          padding: "6px 8px",
          fontSize: 12,
          border: "1px dashed #334155",
          borderRadius: 6,
          background: "#0c121c",
        }}
      >
        {activeEmitterRef?.type === "parent"
          ? "Editing: Parent"
          : activeEmitterRef?.type === "child"
          ? `Editing: Sub-Emitter (${activeEmitterRef.id})`
          : activeEmitterRef?.type === "sequence"
          ? `Editing: Sequencer Step (${activeEmitterRef.stepId})`
          : "Editing"}
      </div>

      <EmitterPanel
        key={`emitter-${remountTick}-${activeEmitterRef?.type}-${
          activeEmitterRef?.id ??
          activeEmitterRef?.stepId ??
          "parent"
        }`}
        emitterShape={scopedEmitterShape}
        setEmitterShape={scopedSetEmitterShape}
        shapeSettings={scopedShapeSettings}
        setShapeSettings={scopedSetShapeSettings}
        showShape={showShape}
        setShowShape={setShowShape}
      />

      <TargetingPanel key={`targeting-${remountTick}`} />

      <SpritePanel
        key={`sprite-${remountTick}-${activeEmitterRef?.type}-${
          activeEmitterRef?.id ??
          activeEmitterRef?.stepId ??
          "parent"
        }`}
        atlas={effectiveAtlas}
        selectedSprite={scopedSelectedSprite}
        setSelectedSprite={scopedSetSelectedSprite}
        selectedSprites={scopedSelectedSprites}
        setSelectedSprites={scopedSetSelectedSprites}
        selectedAnimFrames={
          Array.isArray(scopedEffectSettings?.selectedAnimFrames)
            ? scopedEffectSettings.selectedAnimFrames
            : []
        }
        setSelectedAnimFrames={scopedSetSelectedAnimFrames}
        animFps={Number(
          scopedEffectSettings?.animFps ??
            scoped.animFps ??
            12
        )}
        setAnimFps={scopedSetAnimFps}
        setEffectSettings={scopedSetEffectSettings}
        onMultiChange={(arr) =>
          scopedSetSelectedSprites(arr)
        }
      />

      <ParticlesPanel
        key={`particles-${remountTick}-${activeEmitterRef?.type}-${
          activeEmitterRef?.id ??
          activeEmitterRef?.stepId ??
          "parent"
        }`}
        effectSettings={scopedEffectSettings}
        setEffectSettings={scopedSetEffectSettings}
      />

      <OrbitPanel
        key={`orbit-${remountTick}-${activeEmitterRef?.type}-${
          activeEmitterRef?.id ??
          activeEmitterRef?.stepId ??
          "parent"
        }`}
        effectSettings={scopedEffectSettings}
        setEffectSettings={scopedSetEffectSettings}
      />

      <RotationPanel
        key={`rotation-${remountTick}-${activeEmitterRef?.type}-${
          activeEmitterRef?.id ??
          activeEmitterRef?.stepId ??
          "parent"
        }`}
        effectSettings={scopedEffectSettings}
        setEffectSettings={scopedSetEffectSettings}
      />

      <GravityPanel
        key={`gravity-${remountTick}-${activeEmitterRef?.type}-${
          activeEmitterRef?.id ??
          activeEmitterRef?.stepId ??
          "parent"
        }`}
        effectSettings={scopedEffectSettings}
        setEffectSettings={scopedSetEffectSettings}
      />

      <CollisionPanel
        key={`collision-${remountTick}-${activeEmitterRef?.type}-${
          activeEmitterRef?.id ??
          activeEmitterRef?.stepId ??
          "parent"
        }`}
        atlas={effectiveAtlas}
        effectSettings={scopedEffectSettings}
        setEffectSettings={scopedSetEffectSettings}
      />

      <GlowPanel
        key={`glow-${remountTick}-${activeEmitterRef?.type}-${
          activeEmitterRef?.id ??
          activeEmitterRef?.stepId ??
          "parent"
        }`}
        effectSettings={scopedEffectSettings}
        setEffectSettings={scopedSetEffectSettings}
      />

      <EditorUIPanel
        key={`editorui-${remountTick}`}
        wallsEnabled={wallsEnabled}
        setWallsEnabled={setWallsEnabled}
        tokenEnabled={tokenEnabled}
        setTokenEnabled={setTokenEnabled}
        enemyEnabled={enemyEnabled}
        setEnemyEnabled={setEnemyEnabled}
        showGrid={showGrid}
        setShowGrid={setShowGrid}
        pxPerMeter={pxPerMeter}
        setPxPerMeter={setPxPerMeter}
        showShape={showShape}
        setShowShape={setShowShape}
        onToggleFogHidden={onToggleFogHidden}
        effectSettings={effectSettings}
        setEffectSettings={setEffectSettings}
      />
    </ResizableSidebar>
  );
}

export default EffectsSidebar;
export { EffectsSidebar };
